import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  IconButton,
  CircularProgress,
  Grid,
  Paper,
  Button,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Pagination,
  TextField
} from '@mui/material';
import {
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Comment as CommentIcon,
  Add as AddIcon,
  Sort as SortIcon,
  Forum as ForumIcon,
  Image as ImageIcon,
  CardGiftcard as GiftIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import analyticsService from '../services/analyticsService';
import adImage from '../img/ad-001.png';

// Ad component that appears between review cards
const AdCard = ({ index, adType, onClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Wicked Salem's Candles Ad
  if (adType === 'candles') {
    return (
      <Grid item xs={12}>
        <Card 
          component="a"
          href="https://wickedsalemscandles.com"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ 
            bgcolor: 'rgba(0, 0, 0, 0.7)', 
            color: 'white',
            border: '1px solid rgba(255, 0, 0, 0.3)',
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            textDecoration: 'none',
            display: 'block',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 5px 15px rgba(255, 0, 0, 0.3)',
              borderColor: 'rgba(255, 0, 0, 0.5)',
            }
          }}
          onClick={(e) => {
            onClick();
            e.stopPropagation();
          }}
        >
          <Box sx={{ 
            position: 'absolute', 
            bottom: 10, 
            right: 10, 
            bgcolor: 'transparent', 
            color: 'rgba(255, 255, 255, 0.7)', 
            px: 1, 
            py: 0.5, 
            borderRadius: 1,
            fontSize: '0.7rem',
            fontWeight: 'bold',
            zIndex: 1,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            border: '1px solid rgba(255, 255, 255, 0.5)'
          }}>
            Advertisement
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
            {/* Image section - similar to movie poster */}
            <Box 
              sx={{ 
                width: { xs: '100%', sm: '160px' },
                height: { xs: '180px', sm: '240px' },
                position: 'relative',
                overflow: 'hidden',
                flexShrink: 0,
                margin: { xs: 0, sm: '16px' },
                borderBottom: { xs: '1px solid rgba(255, 0, 0, 0.2)', sm: 'none' }
              }}
            >
              <Box
                component="img"
                src={adImage}
                alt="Wicked Salem's Candle"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'all 0.5s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    filter: 'brightness(1.1)'
                  }
                }}
              />
            </Box>
            
            {/* Content section - similar to movie card content */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ color: '#ff0000', fontWeight: 'bold' }}>
                    Wicked Salem's Candle Company
                  </Typography>
                }
                subheader={
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 'medium' }}>
                    Horror-themed handcrafted candles
                  </Typography>
                }
                sx={{ pt: 2, pb: 1 }}
              />
              
              <CardContent>
                <Typography variant="body2" sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  mt: 1,
                  display: '-webkit-box',
                  overflow: 'hidden',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 3
                }}>
                  Set the perfect atmosphere for your next horror movie night with our spooky yet delightful scented candles. 
                  Made with soy wax and eco-friendly fragrance oils for a cleaner burn.
                </Typography>
              </CardContent>
              
              <CardActions sx={{ 
                px: 2, 
                pb: 2, 
                pt: 0, 
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%'
              }}>
              </CardActions>
            </Box>
          </Box>
        </Card>
      </Grid>
    );
  }
  
  // Default Premium Ad
  return (
    <Grid item xs={12}>
      <Card 
        sx={{ 
          bgcolor: 'rgba(0, 0, 0, 0.7)', 
          color: 'white',
          border: '1px solid rgba(255, 0, 0, 0.3)',
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          height: { xs: 'auto', sm: '120px' },
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 5px 15px rgba(255, 0, 0, 0.3)',
            borderColor: 'rgba(255, 0, 0, 0.5)',
          },
          backgroundImage: 'linear-gradient(to right, rgba(0, 0, 0, 0.8), rgba(20, 20, 20, 0.7))',
        }}
        onClick={(e) => {
          onClick();
          e.stopPropagation();
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            height: '100%', 
            alignItems: 'center', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            px: { xs: 3, sm: 4 },
            py: { xs: 2, sm: 0 },
          }}
        >
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            textAlign: { xs: 'center', sm: 'left' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                color: '#ff0000',
                mb: 0.5, 
                fontSize: { xs: '1rem', sm: '1.25rem' } 
              }}
            >
              Upgrade to Premium
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                maxWidth: { sm: '600px' }
              }}
            >
              Unlock exclusive horror content, personalized recommendations, and ad-free browsing.
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            size="small"
            component={Link}
            to="/subscribe"
            sx={{ 
              bgcolor: '#ff0000', 
              '&:hover': { bgcolor: '#cc0000' },
              minWidth: { xs: '100%', sm: '160px' },
              mt: { xs: 2, sm: 0 },
              textTransform: 'none',
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600
            }}
          >
            UPGRADE
          </Button>
        </Box>
      </Card>
    </Grid>
  );
};

// Shop Ad component to promote the Screamify Shop
const ShopAdComponent = ({ onClick }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Grid item xs={12}>
      <Card 
        sx={{ 
          bgcolor: 'rgba(0, 0, 0, 0.7)', 
          color: 'white',
          border: '1px solid rgba(255, 0, 0, 0.3)',
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          height: { xs: 'auto', sm: '120px' },
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 5px 15px rgba(255, 0, 0, 0.3)',
            borderColor: 'rgba(255, 0, 0, 0.5)',
          },
          backgroundImage: 'linear-gradient(to right, rgba(20, 0, 0, 0.8), rgba(40, 0, 0, 0.7))',
        }}
        onClick={(e) => {
          onClick();
          e.stopPropagation();
          navigate('/shop');
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            height: '100%', 
            alignItems: 'center', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            px: { xs: 3, sm: 4 },
            py: { xs: 2, sm: 0 },
          }}
        >
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            textAlign: { xs: 'center', sm: 'left' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                color: '#ff0000',
                mb: 0.5, 
                fontSize: { xs: '1rem', sm: '1.25rem' } 
              }}
            >
              The Screamify™ Shop
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                maxWidth: { sm: '600px' }
              }}
            >
              Get your exclusive Screamify™ gear! Show your horror passion with our limited edition merchandise.
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/shop');
            }}
            sx={{ 
              bgcolor: '#ff0000', 
              '&:hover': { bgcolor: '#cc0000' },
              minWidth: { xs: '100%', sm: '140px' },
              mt: { xs: 2, sm: 0 },
              textTransform: 'none',
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600
            }}
          >
            Shop Now
          </Button>
        </Box>
      </Card>
    </Grid>
  );
};

// Submission Ad component with email collection form
const SubmissionAd = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Airtable credentials from the documentation
  const AIRTABLE_API_KEY = 'pat4FML0uvc5EtZiq.4b9c4db63db5d87249782d8456c6341da6a25cc41041487340138cd1d4453a99';
  const AIRTABLE_BASE_ID = 'appEp9ztiYve51qqI';
  const AIRTABLE_TABLE_NAME = 'Table%201';
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    
    try {
      const response = await fetch(
        `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${AIRTABLE_TABLE_NAME}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${AIRTABLE_API_KEY}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            records: [
              {
                fields: {
                  "Email": email,
                  "Assignee": new Date().toISOString().split('T')[0] // Today's date in YYYY-MM-DD format
                }
              }
            ]
          })
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        console.error('Airtable Error:', data);
      }
    } catch (error) {
      console.error('Submission Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Grid item xs={12}>
      <Card 
        sx={{ 
          bgcolor: 'rgba(0, 0, 0, 0.7)', 
          color: 'white',
          border: '1px solid rgba(255, 0, 0, 0.3)',
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          padding: 3,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 5px 15px rgba(255, 0, 0, 0.3)',
            borderColor: 'rgba(255, 0, 0, 0.5)',
          },
          backgroundImage: 'linear-gradient(to right, rgba(0, 0, 0, 0.8), rgba(20, 20, 20, 0.7))',
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 'bold',
            color: '#ff0000',
            mb: 1, 
            fontSize: { xs: '1.1rem', sm: '1.3rem' },
            textAlign: 'center'
          }}
        >
          Screamify™ Now Accepting Horror Short Films
        </Typography>
        
        <Typography 
          variant="body2"
          sx={{ 
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: { xs: '0.9rem', sm: '1rem' },
            mb: 2,
            textAlign: 'center'
          }}
        >
          Submit your AI-generated or traditional horror shorts for a chance to be featured on our platform!
        </Typography>
        
        {submitted ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body1" sx={{ color: '#ff0000', fontWeight: 'bold' }}>
              Thank you for your interest!
            </Typography>
            <Typography variant="body2" sx={{ color: 'white', mt: 1 }}>
              We'll contact you with submission details soon.
            </Typography>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              maxWidth: '600px',
              mx: 'auto'
            }}>
              <TextField
                placeholder="Enter your email for submission details"
                variant="outlined"
                type="email"
                required
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 0, 0, 0.5)',
                      borderWidth: 2
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 0, 0, 0.8)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#ff0000',
                    },
                  },
                  '& input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
              />
              <Button 
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ 
                  bgcolor: '#ff0000',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#cc0000'
                  },
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { sm: '160px' }
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'SUBMIT'}
              </Button>
            </Box>
          </Box>
        )}
      </Card>
    </Grid>
  );
};

// ReviewCards component - displays thread/review cards with common functionality
const ReviewCards = ({
  threads,
  loading,
  error,
  onOpenThread,
  onVote,
  onCreateReview,
  getUserVoteStatus,
  canCreate = false,
  title,
  emptyMessage = "No reviews found",
  emptyDescription = "Check back later for new reviews.",
  sortMethod: externalSortMethod,
  onSortChange,
  showAds = false,
  itemsPerPage = 8,
  onPageChange = null
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  // Add pagination state
  const [page, setPage] = useState(1);
  
  // Add sort functionality - use external props if provided, otherwise use internal state
  const [internalSortMethod, setInternalSortMethod] = useState('new');
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  
  // Use external sort method if provided, otherwise use internal state
  const sortMethod = externalSortMethod !== undefined ? externalSortMethod : internalSortMethod;
  
  const handleSortMenuOpen = (event) => {
    setSortMenuAnchor(event.currentTarget);
  };
  
  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };
  
  const handleSortChange = (method) => {
    // If external handler provided, use it
    if (onSortChange) {
      onSortChange(method);
    } else {
      // Otherwise use internal state
      setInternalSortMethod(method);
    }
    handleSortMenuClose();
    // Reset to first page when changing sort
    handleChangePage(null, 1);
  };
  
  // Sort threads based on selected method
  const sortedThreads = useMemo(() => {
    if (!threads || threads.length === 0) return [];
    
    return [...threads].sort((a, b) => {
      if (sortMethod === 'top') {
        const aVotes = (a.upvotes || 0) - (a.downvotes || 0);
        const bVotes = (b.upvotes || 0) - (b.downvotes || 0);
        return bVotes - aVotes;
      } else {
        // Sort by most recent (default)
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });
  }, [threads, sortMethod]);
  
  // Get paginated threads
  const paginatedThreads = useMemo(() => {
    if (!threads || threads.length === 0) return [];
    
    // Calculate the correct number of items to show
    // We want exactly 8 actual review items per page
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    
    // Debug pagination
    console.log(`Pagination: page ${page}, itemsPerPage ${itemsPerPage}, showing items ${startIndex} to ${endIndex-1} out of ${sortedThreads.length}`);
    
    return sortedThreads.slice(startIndex, endIndex);
  }, [sortedThreads, page, itemsPerPage]);
  
  // Calculate total pages
  const totalPages = useMemo(() => {
    if (!threads || threads.length === 0) return 0;
    return Math.ceil(sortedThreads.length / itemsPerPage);
  }, [sortedThreads, itemsPerPage]);
  
  // Handle page change
  const handleChangePage = (event, value) => {
    setPage(value);
    
    // If external page change handler is provided, call it
    if (onPageChange) {
      onPageChange(value);
    }
    
    // Scroll to top of component when page changes
    window.scrollTo({
      top: window.scrollY - 200,
      behavior: 'smooth'
    });
  };

  // Format date to a readable format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Track card click with analytics
  const handleCardClick = (thread) => {
    analyticsService.trackEvent('review_card_click', {
      review_id: thread.threadId,
      review_title: thread.title,
      author: thread.userHandle || 'Anonymous'
    });
    
    // Call the parent component's handler
    onOpenThread(thread);
  };
  
  // Track votes with analytics
  const handleVote = (threadId, itemType, voteType, event) => {
    // Stop propagation to prevent card click
    if (event) {
      event.stopPropagation();
    }
    
    // Track the vote
    analyticsService.trackEvent('review_vote', {
      review_id: threadId,
      vote_type: voteType,
      item_type: itemType
    });
    
    // Call the parent component's handler
    onVote(threadId, itemType, voteType, event);
  };
  
  // Track ad clicks
  const handleAdClick = (adType) => {
    analyticsService.trackEvent('ad_click', {
      ad_type: adType,
      page: 'reviews',
      position: 'card_list'
    });
  };

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: '#ff0000' }} size={60} />
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Typography sx={{ color: '#ff0000', textAlign: 'center', py: 2 }}>
        {error}
      </Typography>
    );
  }

  // Render empty state
  if (!threads || threads.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        p: 4,
        bgcolor: 'rgba(255, 255, 255, 0.03)',
        borderRadius: 2,
        border: '1px dashed rgba(255, 255, 255, 0.1)'
      }}>
        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>{emptyMessage}</Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>
          {emptyDescription}
        </Typography>
      </Box>
    );
  }

  return (
    <Paper 
      elevation={0}
      sx={{ 
        bgcolor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 2,
        p: { xs: 3, sm: 3 },
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}
    >
      {/* Header with title and create button */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
      </Box>

      {/* Sorting options */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', ml: 'auto' }}>
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateReview}
              sx={{
                backgroundColor: '#ff0000',
                color: 'white',
                mr: 2,
                '&:hover': {
                  backgroundColor: '#cc0000',
                }
              }}
            >
              Create Review
            </Button>
          )}
          
          <Button 
            variant="contained"
            endIcon={<SortIcon />}
            onClick={handleSortMenuOpen}
            sx={{
              bgcolor: '#ff0000',
              color: 'white',
              '&:hover': {
                bgcolor: '#cc0000',
              }
            }}
          >
            Sort: {sortMethod === 'top' ? 'Top Rated' : 'Most Recent'}
          </Button>
          <Menu
            anchorEl={sortMenuAnchor}
            open={Boolean(sortMenuAnchor)}
            onClose={handleSortMenuClose}
            PaperProps={{
              sx: {
                bgcolor: 'rgba(30, 30, 30, 0.95)',
                border: '1px solid rgba(255, 0, 0, 0.3)',
                color: 'white'
              }
            }}
          >
            <MenuItem 
              onClick={() => handleSortChange('top')}
              sx={{ 
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255, 0, 0, 0.1)' } 
              }}
            >
              Top Rated
            </MenuItem>
            <MenuItem 
              onClick={() => handleSortChange('new')}
              sx={{ 
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255, 0, 0, 0.1)' } 
              }}
            >
              Most Recent
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Review cards with ads injected at specific positions if showAds is true */}
      <Grid container spacing={3}>
        {paginatedThreads.map((thread, index) => {
          const userVoteStatus = getUserVoteStatus ? getUserVoteStatus('thread', thread.threadId) : null;
          const commentCount = thread.postCount || thread.posts?.length || 0;
          
          return (
            <React.Fragment key={thread.threadId}>
              {/* Review card */}
              <Grid item xs={12}>
                <Card 
                  sx={{ 
                    bgcolor: 'rgba(0, 0, 0, 0.7)', 
                    color: 'white',
                    border: '1px solid rgba(255, 0, 0, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 5px 15px rgba(255, 0, 0, 0.2)',
                      borderColor: 'rgba(255, 0, 0, 0.5)',
                    },
                    borderRadius: 2,
                    overflow: 'hidden',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleCardClick(thread)}
                >
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                    {thread.threadPoster && (
                      <Box 
                        sx={{ 
                          display: 'block',
                          width: { xs: '100%', sm: '160px' },
                          height: { xs: '180px', sm: '240px' },
                          position: 'relative',
                          overflow: 'hidden',
                          flexShrink: 0,
                          margin: { xs: 0, sm: '16px' },
                          borderBottom: { xs: '1px solid rgba(255, 0, 0, 0.2)', sm: 'none' }
                        }}
                      >
                        <Box
                          component="img"
                          src={thread.threadPoster}
                          alt={thread.title}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'all 0.5s ease',
                            '&:hover': {
                              transform: 'scale(1.05)',
                              filter: 'brightness(1.1)'
                            }
                          }}
                        />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <CardHeader
                        title={
                          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {thread.title}
                          </Typography>
                        }
                        subheader={
                          <Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 'medium' }}>
                              By {thread.userHandle || 'Anonymous'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              {formatDate(thread.createdAt)}
                            </Typography>
                          </Box>
                        }
                        sx={{ pt: 2, pb: 1 }}
                      />
                      <CardContent>
                        <Typography variant="body2" sx={{
                          color: 'rgba(255, 255, 255, 0.7)',
                          mt: 1,
                          display: '-webkit-box',
                          overflow: 'hidden',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 3,
                          whiteSpace: 'pre-line'
                        }}>
                          {thread.content}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ px: 2, pb: 2, pt: 0, justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton 
                            onClick={(e) => handleVote(thread.threadId, 'thread', 'up', e)}
                            size="small"
                            sx={{ 
                              color: userVoteStatus === 'up' ? '#ff0000' : 'rgba(255, 255, 255, 0.6)',
                              '&:hover': { color: '#ff0000' }
                            }}
                          >
                            <ThumbUpIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 1.5 }}>
                            {thread.upvotes || 0}
                          </Typography>
                          
                          <IconButton 
                            onClick={(e) => handleVote(thread.threadId, 'thread', 'down', e)}
                            size="small"
                            sx={{ 
                              color: userVoteStatus === 'down' ? '#ff0000' : 'rgba(255, 255, 255, 0.6)', 
                              ml: 1,
                              '&:hover': { color: '#ff0000' }
                            }}
                          >
                            <ThumbDownIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 2 }}>
                            {thread.downvotes || 0}
                          </Typography>
                          
                          {/* Comment count indicator */}
                          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                            <CommentIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)', mr: 0.5 }} />
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              {commentCount}
                            </Typography>
                          </Box>
                        </Box>
                        <Button 
                          variant="text" 
                          size="small"
                          sx={{ color: '#ff0000', '&:hover': { bgcolor: 'rgba(255, 0, 0, 0.1)' } }}
                        >
                          Read More
                        </Button>
                      </CardActions>
                    </Box>
                  </Box>
                </Card>
              </Grid>
              
              {/* Insert ad after card if needed */}
              {showAds && (
                <>
                  {index === 0 && <ShopAdComponent onClick={() => handleAdClick('shop')} />}
                  {index === 1 && <AdCard adType="candles" onClick={() => handleAdClick('candles')} />}
                  {index === 3 && <SubmissionAd />}
                  {index === 5 && <AdCard adType="premium" onClick={() => handleAdClick('premium')} />}
                </>
              )}
            </React.Fragment>
          );
        })}
      </Grid>
      
      {/* Pagination controls */}
      {totalPages > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handleChangePage}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              '& .MuiPaginationItem-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 0, 0, 0.1)'
                }
              },
              '& .Mui-selected': {
                backgroundColor: 'rgba(255, 0, 0, 0.2) !important',
                color: '#fff',
                fontWeight: 'bold'
              }
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default ReviewCards; 